// Polyfill module aliased in craco.config.js to satisfy `from '@mui/material'`
// imports while running on MUI v4. Re-exports v4 components wrapped to accept
// the `sx` prop, plus polyfills for v5-only APIs (Stack, linearProgressClasses).
// Components not listed here are passed through from @material-ui/core unchanged.

import React from 'react';
import * as MuiCore from '@material-ui/core';
import * as MuiLab from '@material-ui/lab';
import { sxToStyle } from './sxToStyle';

const withSx = (Comp) =>
  React.forwardRef(({ sx, style, ...props }, ref) =>
    React.createElement(Comp, {
      ref,
      style: sx ? { ...sxToStyle(sx), ...(style || {}) } : style,
      ...props,
    })
  );

// --- v5-only polyfills -------------------------------------------------------

export const Stack = React.forwardRef(
  (
    {
      direction = 'column',
      spacing = 0,
      divider,
      sx,
      style,
      justifyContent,
      alignItems,
      children,
      ...rest
    },
    ref
  ) => {
    const gap = typeof spacing === 'number' ? spacing * 8 : spacing;
    const items = React.Children.toArray(children).filter((c) => c !== null && c !== false);
    const rendered = divider
      ? items.flatMap((child, i) =>
          i < items.length - 1 ? [child, React.cloneElement(divider, { key: `__div-${i}` })] : [child]
        )
      : items;
    return React.createElement(
      MuiCore.Box,
      {
        ref,
        display: 'flex',
        flexDirection: direction,
        justifyContent,
        alignItems,
        style: {
          gap,
          ...(sx ? sxToStyle(sx) : null),
          ...(style || {}),
        },
        ...rest,
      },
      rendered
    );
  }
);

export const linearProgressClasses = {
  root: 'MuiLinearProgress-root',
  colorPrimary: 'MuiLinearProgress-colorPrimary',
  colorSecondary: 'MuiLinearProgress-colorSecondary',
  determinate: 'MuiLinearProgress-determinate',
  indeterminate: 'MuiLinearProgress-indeterminate',
  bar: 'MuiLinearProgress-bar',
  barColorPrimary: 'MuiLinearProgress-barColorPrimary',
  barColorSecondary: 'MuiLinearProgress-barColorSecondary',
};

// --- v4 components wrapped to accept sx --------------------------------------

export const Box = withSx(MuiCore.Box);
export const Button = withSx(MuiCore.Button);
export const ButtonBase = withSx(MuiCore.ButtonBase);
export const Card = withSx(MuiCore.Card);
export const CardActions = withSx(MuiCore.CardActions);
export const CardContent = withSx(MuiCore.CardContent);
export const Checkbox = withSx(MuiCore.Checkbox);
export const Chip = withSx(MuiCore.Chip);
export const Divider = withSx(MuiCore.Divider);
export const Drawer = withSx(MuiCore.Drawer);
export const FormControl = withSx(MuiCore.FormControl);
export const FormControlLabel = withSx(MuiCore.FormControlLabel);
export const FormGroup = withSx(MuiCore.FormGroup);
export const IconButton = withSx(MuiCore.IconButton);
export const Link = withSx(MuiCore.Link);
export const List = withSx(MuiCore.List);
export const ListItem = withSx(MuiCore.ListItem);
export const ListItemIcon = withSx(MuiCore.ListItemIcon);
export const ListItemText = withSx(MuiCore.ListItemText);
export const MenuItem = withSx(MuiCore.MenuItem);
export const Paper = withSx(MuiCore.Paper);
export const Select = withSx(MuiCore.Select);
export const Slider = withSx(MuiCore.Slider);
export const Switch = withSx(MuiCore.Switch);
export const TextField = withSx(MuiCore.TextField);
export const Typography = withSx(MuiCore.Typography);

// --- v4 hooks / non-component exports — pass through -------------------------

export const useTheme = MuiCore.useTheme;
export const useMediaQuery = MuiCore.useMediaQuery;

// --- components moved from /lab in v4 to /material in v5 ---------------------

export const Alert = withSx(MuiLab.Alert);
export const AlertTitle = MuiLab.AlertTitle;

// --- Snackbar (used by SocketController) -------------------------------------

export const Snackbar = MuiCore.Snackbar;
export const SnackbarContent = MuiCore.SnackbarContent;

// --- catch-all pass-through for anything not explicitly listed above ---------
// Note: components imported by name from here that aren't enumerated will fall
// through to MuiCore via the named re-exports below.

export const AppBar = MuiCore.AppBar;
export const Avatar = MuiCore.Avatar;
export const Backdrop = MuiCore.Backdrop;
export const Badge = MuiCore.Badge;
export const BottomNavigation = MuiCore.BottomNavigation;
export const BottomNavigationAction = MuiCore.BottomNavigationAction;
export const Breadcrumbs = MuiCore.Breadcrumbs;
export const CardActionArea = MuiCore.CardActionArea;
export const CardHeader = MuiCore.CardHeader;
export const CardMedia = MuiCore.CardMedia;
export const CircularProgress = MuiCore.CircularProgress;
export const ClickAwayListener = MuiCore.ClickAwayListener;
export const Collapse = MuiCore.Collapse;
export const Container = MuiCore.Container;
export const CssBaseline = MuiCore.CssBaseline;
export const Dialog = MuiCore.Dialog;
export const DialogActions = MuiCore.DialogActions;
export const DialogContent = MuiCore.DialogContent;
export const DialogContentText = MuiCore.DialogContentText;
export const DialogTitle = MuiCore.DialogTitle;
export const Fab = MuiCore.Fab;
export const Fade = MuiCore.Fade;
export const FilledInput = MuiCore.FilledInput;
export const FormHelperText = MuiCore.FormHelperText;
export const FormLabel = MuiCore.FormLabel;
export const Grid = withSx(MuiCore.Grid);
export const Grow = MuiCore.Grow;
export const Hidden = MuiCore.Hidden;
export const Icon = MuiCore.Icon;
export const Input = MuiCore.Input;
export const InputAdornment = MuiCore.InputAdornment;
export const InputBase = MuiCore.InputBase;
export const InputLabel = MuiCore.InputLabel;
export const LinearProgress = MuiCore.LinearProgress;
export const ListItemAvatar = MuiCore.ListItemAvatar;
export const ListItemSecondaryAction = MuiCore.ListItemSecondaryAction;
export const ListSubheader = MuiCore.ListSubheader;
export const Menu = MuiCore.Menu;
export const MenuList = MuiCore.MenuList;
export const Modal = MuiCore.Modal;
export const NativeSelect = MuiCore.NativeSelect;
export const NoSsr = MuiCore.NoSsr;
export const OutlinedInput = MuiCore.OutlinedInput;
export const Popover = MuiCore.Popover;
export const Popper = MuiCore.Popper;
export const Portal = MuiCore.Portal;
export const Radio = MuiCore.Radio;
export const RadioGroup = MuiCore.RadioGroup;
export const RootRef = MuiCore.RootRef;
export const Slide = MuiCore.Slide;
export const Step = MuiCore.Step;
export const StepButton = MuiCore.StepButton;
export const StepConnector = MuiCore.StepConnector;
export const StepContent = MuiCore.StepContent;
export const StepIcon = MuiCore.StepIcon;
export const StepLabel = MuiCore.StepLabel;
export const Stepper = MuiCore.Stepper;
export const SvgIcon = MuiCore.SvgIcon;
export const SwipeableDrawer = MuiCore.SwipeableDrawer;
export const Tab = MuiCore.Tab;
export const Table = withSx(MuiCore.Table);
export const TableBody = MuiCore.TableBody;
export const TableCell = withSx(MuiCore.TableCell);
export const TableContainer = withSx(MuiCore.TableContainer);
export const TableFooter = MuiCore.TableFooter;
export const TableHead = MuiCore.TableHead;
export const TablePagination = MuiCore.TablePagination;
export const TableRow = withSx(MuiCore.TableRow);
export const TableSortLabel = MuiCore.TableSortLabel;
export const Tabs = MuiCore.Tabs;
export const Toolbar = MuiCore.Toolbar;
export const Tooltip = MuiCore.Tooltip;
export const Zoom = MuiCore.Zoom;

// StyledEngineProvider stub (v5-only — no-op on v4)
export const StyledEngineProvider = ({ children }) => children;
